import type { Enums } from "@/integrations/supabase/types";

type AppRole = Enums<"app_role">;

const CRISP_SCRIPT_ID = "crisp-chat-sdk";
const CRISP_POSITION_STYLE_ID = "crisp-chat-position-style";

/** Décalage au-dessus de la safe area — navigateur (mobile, tablette, desktop). */
const CRISP_LAUNCHER_OFFSET_BROWSER_PX = 76;
/**
 * App installée (display-mode `standalone` ou `window-controls-overlay`) : léger ajustement vs onglet navigateur.
 * À modifier si la bulle est encore trop haute ou trop basse en PWA uniquement.
 */
const CRISP_LAUNCHER_OFFSET_PWA_PX = 80;
type CrispUserContext = {
  email: string | null;
  displayName: string | null;
  role: AppRole | null;
  storeName: string | null;
};

declare global {
  interface Window {
    $crisp?: unknown[];
    CRISP_WEBSITE_ID?: string;
  }
}

function getRoleLabel(role: AppRole | null): string {
  switch (role) {
    case "assistant_manager":
      return "adjoint";
    case "department_head":
      return "directeur";
    case "franchisee":
      return "owner";
    case "employee":
      return "employé";
    default:
      return "autre";
  }
}

function ensurePositionStyle(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(CRISP_POSITION_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = CRISP_POSITION_STYLE_ID;
  style.textContent = `
    #crisp-chatbox,
    .crisp-client,
    iframe[src*="crisp.chat"] {
      bottom: calc(env(safe-area-inset-bottom, 0px) + ${CRISP_LAUNCHER_OFFSET_BROWSER_PX}px) !important;
    }

    @media (display-mode: standalone), (display-mode: window-controls-overlay) {
      #crisp-chatbox,
      .crisp-client,
      iframe[src*="crisp.chat"] {
        bottom: calc(env(safe-area-inset-bottom, 0px) + ${CRISP_LAUNCHER_OFFSET_PWA_PX}px) !important;
      }
    }
  `;

  document.head.appendChild(style);
}

function ensureCrispScript(websiteId: string): void {
  window.$crisp = window.$crisp ?? [];
  window.CRISP_WEBSITE_ID = websiteId;

  if (document.getElementById(CRISP_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement("script");
  script.id = CRISP_SCRIPT_ID;
  script.src = "https://client.crisp.chat/l.js";
  script.async = true;
  document.head.appendChild(script);
}

function configureCrispUser({ email, displayName, role, storeName }: CrispUserContext): void {
  if (!window.$crisp) return;

  if (email) {
    window.$crisp.push(["set", "user:email", [email]]);
  }

  if (displayName) {
    window.$crisp.push(["set", "user:nickname", [displayName]]);
  }

  window.$crisp.push([
    "set",
    "session:data",
    [[["role", getRoleLabel(role)], ["magasin", storeName ?? "inconnu"], ["langue", "fr"]]],
  ]);
}

/** Rôles autorisés à voir le widget Crisp (pas les salariés « employé »). */
const CRISP_ELIGIBLE_ROLES: readonly AppRole[] = [
  "franchisee",
  "department_head",
  "assistant_manager",
];

export function canUseCrisp(role: AppRole | null): boolean {
  if (!role) return false;
  return (CRISP_ELIGIBLE_ROLES as readonly AppRole[]).includes(role);
}

export function enableCrisp(websiteId: string, userContext: CrispUserContext): void {
  if (typeof window === "undefined") return;
  ensurePositionStyle();
  ensureCrispScript(websiteId);
  window.$crisp?.push(["do", "chat:show"]);
  configureCrispUser(userContext);
}

/**
 * Ouvre la conversation Crisp, avec un brouillon de message si fourni.
 * Renvoie `false` quand le chat n'est pas chargé pour ce compte (salarié, clé absente).
 */
export function openSupportChat(draft?: string): boolean {
  if (typeof window === "undefined" || !window.$crisp || !document.getElementById(CRISP_SCRIPT_ID)) return false;
  if (draft) window.$crisp.push(["set", "message:text", [draft]]);
  window.$crisp.push(["do", "chat:show"]);
  window.$crisp.push(["do", "chat:open"]);
  return true;
}

export function disableCrisp(): void {
  if (typeof window === "undefined") return;
  // Toujours pousser sur la file (même si le script n'est pas encore chargé).
  window.$crisp = window.$crisp ?? [];
  // session:reset peut rétablir la visibilité par défaut du widget : fermer la session
  // puis forcer le masquage en dernier (voir doc Crisp « session:reset » / « chat:hide »).
  window.$crisp.push(["do", "chat:close"]);
  window.$crisp.push(["do", "session:reset"]);
  window.$crisp.push(["do", "chat:hide"]);
}
